import React, { useMemo } from 'react';
import { Group, Line, Rect, Text } from 'react-konva';
import type { DistributionGapSegment } from '../../../domains/diagram/models/DistributionGuides';
import { useThemeMode } from '../../../react-app/theme/ThemeProvider';

interface DistributionGuidesOverlayProps {
  segments: DistributionGapSegment[];
}

export const DistributionGuidesOverlay: React.FC<DistributionGuidesOverlayProps> = ({ segments }) => {
  const { mode } = useThemeMode();
  const normalized = useMemo(() => {
    return segments
      .filter((s) => Number.isFinite(s.gap) && s.gap > 0)
      .map((s) => ({
        ...s,
        gapText: String(Math.round(s.gap)),
      }));
  }, [segments]);

  if (normalized.length === 0) {
    return null;
  }

  const stroke = '#94A3B8';
  const strokeWidth = 1;
  const dash = [4, 4];
  const endCap = 6;
  const labelPadX = 10;
  const labelPadY = 4;
  const labelFontSize = 12;
  const labelCharWidth = 6.5;
  const labelOffset = 10;
  const labelCornerRadius = 4;
  const labelBg = mode === 'dark' ? '#9DC4F8' : '#08436B';
  const labelFg = mode === 'dark' ? '#0B1020' : '#FFFFFF';

  return (
    <Group listening={false}>
      {normalized.map((s) => {
        const isHorizontal = s.orientation === 'horizontal';
        const start = s.start;
        const end = s.end;

        const mid = isHorizontal
          ? { x: (start.x + end.x) / 2, y: start.y - labelOffset }
          : { x: start.x - labelOffset, y: (start.y + end.y) / 2 };

        const labelWidth = Math.max(24, s.gapText.length * labelCharWidth + labelPadX * 2);
        const labelHeight = labelFontSize * 1.2 + labelPadY * 2;
        const labelX = mid.x - labelWidth / 2;
        const labelY = mid.y - labelHeight / 2;

        const cap1 = isHorizontal
          ? [start.x, start.y - endCap, start.x, start.y + endCap]
          : [start.x - endCap, start.y, start.x + endCap, start.y];

        const cap2 = isHorizontal
          ? [end.x, end.y - endCap, end.x, end.y + endCap]
          : [end.x - endCap, end.y, end.x + endCap, end.y];

        return (
          <Group key={s.id} name="distribution-guide">
            <Line
              points={[start.x, start.y, end.x, end.y]}
              stroke={stroke}
              strokeWidth={strokeWidth}
              dash={dash}
              dashEnabled
              opacity={0.9}
              perfectDrawEnabled={false}
              listening={false}
            />
            <Line
              points={cap1}
              stroke={stroke}
              strokeWidth={strokeWidth}
              opacity={0.9}
              perfectDrawEnabled={false}
              listening={false}
            />
            <Line
              points={cap2}
              stroke={stroke}
              strokeWidth={strokeWidth}
              opacity={0.9}
              perfectDrawEnabled={false}
              listening={false}
            />
            {s.showLabel && (
              <Group listening={false}>
                <Rect
                  x={labelX}
                  y={labelY}
                  width={labelWidth}
                  height={labelHeight}
                  fill={labelBg}
                  opacity={1}
                  cornerRadius={labelCornerRadius}
                  perfectDrawEnabled={false}
                  listening={false}
                />
                <Text
                  x={labelX}
                  y={labelY}
                  width={labelWidth}
                  height={labelHeight}
                  align="center"
                  verticalAlign="middle"
                  text={s.gapText}
                  fontSize={labelFontSize}
                  fill={labelFg}
                  listening={false}
                  perfectDrawEnabled={false}
                />
              </Group>
            )}
          </Group>
        );
      })}
    </Group>
  );
};

